import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "../../styles/components/SettingsModal.module.css";
import { useAuth } from "../../contexts/AuthContext";
import { useLeadContext } from "../../contexts/LeadContext";
import SettingsSidebar from "./SettingsSidebar";
import ProfileSection from "./sections/ProfileSection";
import PreferencesSection from "./sections/PreferencesSection";
import NotificationsSection from "./sections/NotificationsSection";
import LinkedAccountsSection from "./sections/LinkedAccountsSection";
import PaymentPlansSection from "./sections/PaymentPlansSection";
import BillingSection from "./sections/BillingSection";
import CreditsSection from "./sections/CreditsSection";
import PrivacyDataSection from "./sections/PrivacyDataSection";
import { getAccountSummary } from "../../services/billing.service";

const menuItems = [
  { id: "perfil", label: "Perfil" },
  { id: "privacidad", label: "Privacidad de datos" },
  { id: "preferencias", label: "Preferencias" },
  { id: "notificaciones", label: "Notificaciones" },
  { id: "cuentas-linkeadas", label: "Cuentas linkeadas" },
  { id: "planes", label: "Planes de pago" },
  { id: "facturacion", label: "Facturación" },
  { id: "creditos", label: "Créditos" },
];

const billingSectionIds = ["planes", "facturacion", "creditos"];

const SettingsModal = ({ isOpen = false, initialSection = null, onClose }) => {
  const { auth } = useAuth() || {};
  const { supervisedAccounts } = useLeadContext() || {};

  const [activeSection, setActiveSection] = useState("perfil");

  useEffect(() => {
    if (isOpen && initialSection && menuItems.some((item) => item.id === initialSection)) {
      setActiveSection(initialSection);
    }
  }, [isOpen, initialSection]);
  const [selectedPlansAccount, setSelectedPlansAccount] = useState(null);
  const [selectedBillingAccount, setSelectedBillingAccount] = useState(null);
  const [accountSummary, setAccountSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const billableAccounts = useMemo(() => {
    if (!Array.isArray(supervisedAccounts)) return [];

    const manageableAccountIds = new Set(
      (auth?.billingAccounts || [])
        .filter((billingAccount) => billingAccount?.can_manage_billing)
        .map((billingAccount) =>
          Number(billingAccount.billing_account_id ?? billingAccount.id),
        ),
    );

    return supervisedAccounts.filter(
      (account) =>
        account?.father_account === null &&
        manageableAccountIds.has(Number(account?.id)),
    );
  }, [auth?.billingAccounts, supervisedAccounts]);

  const singleBillableAccount =
    billableAccounts.length === 1 ? billableAccounts[0] : null;

  const visibleMenuItems = useMemo(() => {
    if (billableAccounts.length > 0) return menuItems;
    return menuItems.filter((item) => !billingSectionIds.includes(item.id));
  }, [billableAccounts.length]);

  useEffect(() => {
    if (billableAccounts.length === 0 && billingSectionIds.includes(activeSection)) {
      setActiveSection("perfil");
    }
  }, [billableAccounts.length, activeSection]);

  useEffect(() => {
    const billableAccountIds = new Set(
      billableAccounts.map((account) => account.id),
    );

    if (
      selectedPlansAccount &&
      !billableAccountIds.has(selectedPlansAccount.id)
    ) {
      setSelectedPlansAccount(null);
    }
    if (
      selectedBillingAccount &&
      !billableAccountIds.has(selectedBillingAccount.id)
    ) {
      setSelectedBillingAccount(null);
    }
  }, [billableAccounts, selectedPlansAccount, selectedBillingAccount]);

  useEffect(() => {
    if (!singleBillableAccount) return;
    if (activeSection === "planes" && !selectedPlansAccount) {
      setSelectedPlansAccount(singleBillableAccount);
    }
    if (activeSection === "facturacion" && !selectedBillingAccount) {
      setSelectedBillingAccount(singleBillableAccount);
    }
  }, [
    singleBillableAccount,
    activeSection,
    selectedPlansAccount,
    selectedBillingAccount,
  ]);

  const activeAccountId = useMemo(() => {
    if (activeSection === "facturacion") return selectedBillingAccount?.id ?? null;
    if (activeSection === "planes") return selectedPlansAccount?.id ?? null;
    return null;
  }, [activeSection, selectedBillingAccount, selectedPlansAccount]);

  useEffect(() => {
    if (!activeAccountId) {
      setAccountSummary(null);
      return;
    }

    let cancelled = false;

    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        const data = await getAccountSummary(activeAccountId);
        if (!cancelled) setAccountSummary(data);
      } catch {
        if (!cancelled) setAccountSummary(null);
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };

    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [activeAccountId]);

  const refreshSummary = useCallback(async () => {
    if (!activeAccountId) return;
    setSummaryLoading(true);
    try {
      const data = await getAccountSummary(activeAccountId);
      setAccountSummary(data);
    } catch {
      // silently fail on manual refresh
    } finally {
      setSummaryLoading(false);
    }
  }, [activeAccountId]);

  const sectionTitle = useMemo(() => {
    return menuItems.find((item) => item.id === activeSection)?.label || "Perfil";
  }, [activeSection]);

  const handleSelectSection = (sectionId) => {
    setActiveSection(sectionId);
    if (sectionId !== "planes") setSelectedPlansAccount(null);
    if (sectionId !== "facturacion") setSelectedBillingAccount(null);
  };

  const handleBackFromPlans = () => {
    setSelectedPlansAccount(null);
  };

  const handleBackFromBilling = () => {
    setSelectedBillingAccount(null);
  };

  const renderHeaderTitle = () => {
    if (activeSection === "planes" && selectedPlansAccount && !singleBillableAccount) {
      return (
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBackFromPlans}
          >
            ←
          </button>
          <div className={styles.accountTitle}>
            {selectedPlansAccount.name || "Sin nombre"}
          </div>
        </div>
      );
    }

    if (activeSection === "facturacion" && selectedBillingAccount && !singleBillableAccount) {
      return (
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBackFromBilling}
          >
            ←
          </button>
          <div className={styles.accountTitle}>
            {selectedBillingAccount.name || "Sin nombre"}
          </div>
        </div>
      );
    }

    return <h2 className={styles.title}>{sectionTitle}</h2>;
  };

  const renderSection = () => {
    switch (activeSection) {
      case "perfil":
        return <ProfileSection />;

      case "privacidad":
        return <PrivacyDataSection accounts={supervisedAccounts} />;

      case "preferencias":
        return <PreferencesSection />;

      case "notificaciones":
        return <NotificationsSection />;

      case "cuentas-linkeadas":
        return <LinkedAccountsSection />;

      case "planes":
        return (
          <PaymentPlansSection
            accounts={billableAccounts}
            selectedAccount={selectedPlansAccount}
            onSelectedAccountChange={setSelectedPlansAccount}
            onBack={handleBackFromPlans}
            summary={accountSummary}
            refreshSummary={refreshSummary}
          />
        );

      case "facturacion":
        return (
          <BillingSection
            accounts={billableAccounts}
            selectedAccount={selectedBillingAccount}
            onSelectedAccountChange={setSelectedBillingAccount}
            summary={accountSummary}
            summaryLoading={summaryLoading}
            refreshSummary={refreshSummary}
          />
        );

      case "creditos":
        return <CreditsSection accounts={billableAccounts} />;

      default:
        return <ProfileSection />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <SettingsSidebar
          items={visibleMenuItems}
          activeSection={activeSection}
          onSelect={handleSelectSection}
        />

        <section className={styles.content}>
          <div className={styles.header}>
            {renderHeaderTitle()}

            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
            >
              ×
            </button>
          </div>

          <div className={styles.body}>{renderSection()}</div>
        </section>
      </div>
    </div>
  );
};

export default SettingsModal;
